import api from '../utils/api';
import { User, Class, UserRegistrationData } from '../types';
import { getAllClasses } from './Classes';

// Normalize a user object from API into our frontend shape
const normalizeUser = (u: any): User => {
  if (!u) return u as User;
  const out: any = { ...u };
  
  // Normalize ID
  if (out._id && !out.id) out.id = out._id;
  
  // Normalize firstName/lastName from lowercase variants
  if (out.firstname !== undefined && out.firstName === undefined) out.firstName = out.firstname;
  if (out.lastname !== undefined && out.lastName === undefined) out.lastName = out.lastname;
  
  // If firstName/lastName are still missing but we have fullName, parse it
  if ((!out.firstName || !out.lastName) && out.fullName) {
    const nameParts = out.fullName.trim().split(/\s+/);
    if (nameParts.length > 0) {
      if (!out.firstName) out.firstName = nameParts[0];
      if (!out.lastName) out.lastName = nameParts.slice(1).join(' ') || nameParts[0];
    }
  }
  
  return out as User;
};

// Get all users (admin only)
export const getAllUsers = async (): Promise<User[]> => {
  const res = await api.get<User[]>('/api/users');
  return (res.data || []).map(normalizeUser);
};

// Get users registered this month
export const getUsersRegisteredThisMonth = async (): Promise<User[]> => {
  const res = await api.get<User[]>('/api/users/registered-this-month');
  return (res.data || []).map(normalizeUser);
};

// Search users by email (partial, case-insensitive)
export const searchUsersByEmail = async (email: string): Promise<User[]> => {
  const res = await api.get<User[]>(`/api/users/search?email=${encodeURIComponent(email)}`);
  return (res.data || []).map(normalizeUser);
};

export interface UserFilterParams {
  stateID?: number;
  professionID?: string;
  email?: string;
  suspended?: boolean;
  role?: 'admin' | 'user';
  showDeleted?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface UserFilterResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: {
    totalUsers: number;
    activeUsers: number;
    suspendedUsers: number;
    filteredCount: number;
  };
}

// Advanced user filtering with pagination
export const filterUsers = async (params: UserFilterParams): Promise<UserFilterResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params.stateID !== undefined) queryParams.append('stateID', params.stateID.toString());
  if (params.professionID) queryParams.append('professionID', params.professionID);
  if (params.email) queryParams.append('email', params.email);
  if (params.suspended !== undefined) queryParams.append('suspended', params.suspended.toString());
  if (params.role) queryParams.append('role', params.role);
  if (params.showDeleted) queryParams.append('showDeleted', 'true');
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  
  const res = await api.get<UserFilterResponse>(`/api/users/filter?${queryParams.toString()}`);
  
  // Normalize users
  const normalized = {
    ...res.data,
    users: (res.data.users || []).map(normalizeUser),
  };
  
  return normalized;
};


// Delete a user by ID
export const deleteUser = async (userId: string): Promise<void> => {
  await api.delete(`/api/users/${userId}`);
};

// Restore a soft-deleted user
export const restoreUser = async (userId: string): Promise<void> => {
  await api.put(`/api/users/${userId}/restore`);
};

// Admin: Create a new user
export const createUser = async (user: UserRegistrationData & Partial<User>) => {
  // Backend should accept at least email, password, firstName, lastName, phone, role
  const res = await api.post('/api/users', user);
  return res.data;
};

// Suspend or unsuspend a user
export const suspendUser = async (userId: string, suspended: boolean = true): Promise<void> => {
  await api.patch(`/api/users/${userId}/suspend`, { suspended });
};

// Update a user's role
export const updateUserRole = async (userId: string, role: 'admin' | 'user'): Promise<void> => {
  await api.patch(`/api/users/${userId}/role`, { role });
};

export const getUserProfile = async (userId: string): Promise<User> => {
  try {
    const res = await api.get(`/api/users/${userId}`);
    console.log('Raw API response for user:', res.data);
    console.log('Raw professionID:', res.data.professionID);
    const normalized = normalizeUser(res.data);
    console.log('Normalized user:', normalized);
    console.log('Normalized professionID:', normalized.professionID);
    return normalized;
  } catch (error) {
    throw error;
  }
};

export interface PendingClassesResult { items: Class[]; hint?: string }

export const getPendingClassesForUser = async (userId: string): Promise<PendingClassesResult> => {
  try {
    const res = await api.get(`/api/users/${userId}/pending-classes`);
    const items = (res.data?.pendingClasses as Class[]) || [];
    return { items };
  } catch (err: any) {
    // If the endpoint doesn't exist (404), return empty list with hint to avoid leaking other users' classes
    if (err?.response?.status === 404) {
      console.warn('Endpoint /api/users/:id/pending-classes not found. Returning empty pending list to avoid showing other users\' classes.');
      return { items: [], hint: 'Pending classes are not available yet.' };
    }
    throw err;
  }
};

export const updateUserProfile = async (userId: string, updates: Partial<User>): Promise<User> => {
  try {
    const res = await api.put(`/api/users/${userId}`, updates);
    return normalizeUser(res.data);
  } catch (error) {
    console.error('API: Error updating user profile:', error);
    throw error;
  }
};

// Get impact analysis for deleting a license
export interface LicenseDeleteImpact {
  license: {
    stateID: number;
    licenseNumber: string;
  };
  impactSummary: {
    cyclesCount: number;
    assignmentsCount: number;
    cycleDetails: Array<{
      _id: string;
      cyclename: string;
      cyclestart?: Date;
      cycleend?: Date;
      professionID: string;
    }>;
  };
}

export const getLicenseDeleteImpact = async (licenseId: string): Promise<LicenseDeleteImpact> => {
  try {
    const res = await api.get(`/api/users/me/licenses/${licenseId}/delete-impact`);
    return res.data;
  } catch (error) {
    console.error('API: Error getting license delete impact:', error);
    throw error;
  }
};

// Delete license with password confirmation and cascade delete
export interface LicenseDeleteResult {
  message: string;
  deletedCycles: number;
  deletedAssignments: number;
  updatedClasses: number;
}

export const deleteLicenseWithPassword = async (licenseId: string, password: string): Promise<LicenseDeleteResult> => {
  try {
    const res = await api.post(`/api/users/me/licenses/${licenseId}/delete-with-password`, { password });
    return res.data;
  } catch (error) {
    console.error('API: Error deleting license:', error);
    throw error;
  }
};

// ─── One-Time Completion Records ──────────────────────────────────────────────

export interface OneTimeCompletion {
  _id?: string;
  categoryTag: string;
  professionID: string;
  stateID: number;
  creditHoursEarned?: number;
  completedDate?: string;
  notes?: string;
}

export const addOneTimeCompletion = async (
  userId: string,
  data: Omit<OneTimeCompletion, '_id'>
): Promise<OneTimeCompletion> => {
  const res = await api.post(`/api/users/${userId}/one-time-completions`, data);
  return res.data;
};

export const deleteOneTimeCompletion = async (userId: string, completionId: string): Promise<void> => {
  await api.delete(`/api/users/${userId}/one-time-completions/${completionId}`);
};